import React, { useEffect, useState } from 'react';
import useStore from './store';
import { version } from '../package.json';
import { Activity, Wifi, WifiOff, Cpu, Globe, Lock, AlertCircle, Settings, MapPin, Monitor, Terminal } from 'lucide-react';

import Sidebar from './components/Sidebar';
import MirrorView from './components/MirrorView';
import DevToolsPanel from './components/DevToolsPanel';
import LogPanel from './components/LogPanel';
import ErrorBoundary from './components/ErrorBoundary';
import DoudianDashboard from './components/DoudianDashboard';

function App() {
  const {
    connect, isConnected, connectToTarget,
    onlineTargets, targetId, disconnect,
    getNetworkView, getBusinessView, sendCommand
  } = useStore();

  const [viewMode, setViewMode] = useState('NETWORK'); // 'NETWORK' or 'BUSINESS'
  const [bridgeUrl, setBridgeUrl] = useState('wss://aether-guard-server.sunnyorange.workers.dev');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [showDevTools, setShowDevTools] = useState(false); // DevTools drawer state

  // Predefined Bridge options
  const bridgePresets = [
    { label: 'Official', url: 'wss://ws.aijiakefu.com', icon: '🏢' },
    { label: 'Cloudflare', url: 'wss://aether-guard-server.sunnyorange.workers.dev', icon: '⚡' },
    { label: 'Local', url: 'ws://localhost:8080', icon: '🏠' },
    { label: 'Render', url: 'wss://aether-bridge.onrender.com', icon: '☁️' }
  ];

  // Auto-connect to official bridge on mount
  useEffect(() => {
    const officialServer = 'wss://ws.aijiakefu.com';
    connect(officialServer);
    setBridgeUrl(officialServer);
  }, []);

  const connectedTarget = onlineTargets.find(t => t.id === targetId);

  return (
    <ErrorBoundary>
      <div className="h-screen bg-gray-950 text-gray-100 font-sans flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-12 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 shrink-0 z-20">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600/20 rounded-lg flex items-center justify-center border border-blue-500/30">
              <Activity className="text-blue-400" size={18} />
            </div>
            <div>
              <h1 className="text-sm font-bold tracking-wide text-gray-100">AETHER STUDIO</h1>
              <div className="text-[10px] text-gray-500 font-mono leading-none">v{version}</div>
            </div>
          </div>

          {/* Target Status Bar */}
          {connectedTarget ? (
            <div className="flex items-center gap-6 px-4 py-1.5 bg-gray-800/50 rounded-full border border-gray-800">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-gray-200">{connectedTarget.id}</span>
              </div>
              <div className="w-px h-3 bg-gray-700" />
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex items-center gap-1"><Globe size={12} /> {connectedTarget.metadata?.clientIp || 'Unknown IP'}</span>
                <span className="flex items-center gap-1"><MapPin size={12} /> {connectedTarget.metadata?.ipLocation || 'Unknown Loc'}</span>
                <span className="flex items-center gap-1"><Monitor size={12} /> {connectedTarget.metadata?.shopName || connectedTarget.metadata?.platform}</span>
              </div>
              <button
                onClick={disconnect}
                className="ml-2 text-[10px] text-red-400 hover:text-red-300 hover:underline"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <div className="text-xs text-gray-600 italic">No Active Target</div>
          )}

          <div className="flex items-center gap-4">
            {/* Bridge URL Config */}
            <div className="relative">
              <button
                onClick={() => setShowUrlInput(!showUrlInput)}
                className="text-xs px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded border border-gray-700 transition-colors flex items-center gap-2"
                title="Configure Bridge URL"
              >
                <span>{bridgePresets.find(p => p.url === bridgeUrl)?.icon || '🔗'}</span>
                <span className="max-w-[200px] truncate">{bridgeUrl}</span>
              </button>

              {showUrlInput && (
                <div className="absolute right-0 top-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-4 w-96 z-50">
                  <div className="text-sm font-medium text-gray-200 mb-3">Bridge Server</div>

                  {/* Quick Select Buttons */}
                  <div className="flex gap-2 mb-3">
                    {bridgePresets.map((preset) => (
                      <button
                        key={preset.label}
                        onClick={() => setBridgeUrl(preset.url)}
                        className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${bridgeUrl === preset.url
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-900 text-gray-300 hover:bg-gray-700'
                          }`}
                      >
                        <div className="text-base mb-1">{preset.icon}</div>
                        <div>{preset.label}</div>
                        <div className="text-[10px] opacity-60 mt-1 truncate">{preset.url}</div>
                      </button>
                    ))}
                  </div>

                  {/* Custom URL Input */}
                  <div className="text-xs text-gray-400 mb-2">Custom URL</div>
                  <input
                    type="text"
                    value={bridgeUrl}
                    onChange={(e) => setBridgeUrl(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-xs text-gray-200 focus:outline-none focus:border-blue-500 font-mono"
                    placeholder="ws://localhost:8080 or wss://your-bridge.com"
                  />

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setShowUrlInput(false)}
                      className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-xs rounded transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        connect(bridgeUrl);
                        setShowUrlInput(false);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors font-medium"
                    >
                      Connect
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={`flex items-center gap-2 text-xs ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
              <Wifi size={14} />
              <span>{isConnected ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </header>

        {/* Main Workspace */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar */}
          <Sidebar
            viewMode={viewMode}
            setViewMode={setViewMode}
            networkData={getNetworkView()}
            businessData={getBusinessView()}
            selectedId={targetId}
            onSelectTarget={connectToTarget}
            onSelectSession={(probeId, tabId) => {
              connectToTarget(probeId);
              sendCommand('SWITCH_TAB', { tabId });
            }}
          />

          {/* Center Stage */}
          <div className="flex-1 flex flex-col min-w-0 bg-gray-950 relative">
            {viewMode === 'DOUDIAN' ? (
              <DoudianDashboard />
            ) : (
              <>
                <MirrorView />
                {/* Bottom Logs */}
                <LogPanel />
              </>
            )}

            {/* Floating DevTools Trigger Button */}
            <button
              onClick={() => setShowDevTools(!showDevTools)}
              className="fixed right-6 bottom-20 z-30 p-4 bg-blue-600 hover:bg-blue-700 rounded-full shadow-2xl transition-all hover:scale-110"
              title="Toggle DevTools (Ctrl+K)"
            >
              <Terminal size={20} className="text-white" />
            </button>
          </div>

          {/* Right DevTools - Drawer Mode */}
          <div className={`fixed right-0 top-12 bottom-0 w-96 bg-gray-900 border-l border-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out z-40 ${showDevTools ? 'translate-x-0' : 'translate-x-full'
            }`}>
            <DevToolsPanel />
          </div>

          {/* Overlay when drawer is open */}
          {showDevTools && (
            <div
              className="fixed inset-0 bg-black/50 z-30"
              onClick={() => setShowDevTools(false)}
            />
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
